import { useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { cn } from '@/lib/utils';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  priority?: boolean;
  className?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  aspectRatio?: string;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * OptimizedImage component with lazy loading, blur placeholder, and responsive support
 *
 * Features:
 * - Lazy loading for better performance
 * - Blur-up effect while loading
 * - Automatic WebP support (when backend provides it)
 * - Responsive srcset support (when backend provides it)
 * - Error fallback handling
 * - Priority loading for above-the-fold images
 *
 * Usage:
 * ```tsx
 * <OptimizedImage
 *   src="/images/property.jpg"
 *   alt="Property image"
 *   priority={true} // For hero images
 *   className="w-full h-64"
 * />
 * ```
 */
export function OptimizedImage({
  src,
  alt,
  width,
  height,
  priority = false,
  className,
  objectFit = 'cover',
  aspectRatio,
  onLoad,
  onError,
}: OptimizedImageProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Fallback image for errors
  const fallbackImage = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23e5e7eb" width="400" height="300"/%3E%3Ctext fill="%236b7280" font-family="sans-serif" font-size="16" dy="10.5" font-weight="400" x="50%25" y="50%25" text-anchor="middle"%3EImagen no disponible%3C/text%3E%3C/svg%3E';

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad?.();
  };

  const handleError = () => {
    setImageError(true);
    onError?.();
  };

  // Extract filename and check if it's from Unsplash
  const isUnsplash = src.includes('unsplash.com');

  // Generate responsive srcset for Unsplash images
  const getSrcSet = () => {
    if (!isUnsplash) return undefined;

    // Unsplash URL parameters for different sizes
    return `${src}&w=400 400w, ${src}&w=800 800w, ${src}&w=1200 1200w`;
  };

  // Generate sizes attribute
  const getSizes = () => {
    if (!isUnsplash) return undefined;

    return '(max-width: 640px) 400px, (max-width: 1024px) 800px, 1200px';
  };

  // For priority images (hero, featured), load immediately without lazy loading
  if (priority) {
    return (
      <picture>
        {/* WebP source - will be used when backend provides .webp versions */}
        {!imageError && src.match(/\.(jpg|jpeg|png)$/i) && (
          <source
            srcSet={src.replace(/\.(jpg|jpeg|png)$/i, '.webp')}
            type="image/webp"
          />
        )}

        {/* Original format */}
        <img
          src={imageError ? fallbackImage : src}
          srcSet={!imageError && isUnsplash ? getSrcSet() : undefined}
          sizes={!imageError && isUnsplash ? getSizes() : undefined}
          alt={alt}
          width={width}
          height={height}
          loading="eager"
          className={cn(
            'transition-opacity duration-300',
            isLoaded ? 'opacity-100' : 'opacity-0',
            className
          )}
          style={{
            objectFit,
            aspectRatio: aspectRatio || 'auto',
          }}
          onLoad={handleLoad}
          onError={handleError}
        />
      </picture>
    );
  }

  // For non-priority images, use lazy loading
  return (
    <picture>
      {/* WebP source - will be used when backend provides .webp versions */}
      {!imageError && src.match(/\.(jpg|jpeg|png)$/i) && (
        <source
          srcSet={src.replace(/\.(jpg|jpeg|png)$/i, '.webp')}
          type="image/webp"
        />
      )}

      {/* Lazy loaded image with blur effect */}
      <LazyLoadImage
        src={imageError ? fallbackImage : src}
        srcSet={!imageError && isUnsplash ? getSrcSet() : undefined}
        sizes={!imageError && isUnsplash ? getSizes() : undefined}
        alt={alt}
        width={width}
        height={height}
        effect="blur"
        threshold={100}
        className={cn(className)}
        style={{
          objectFit,
          aspectRatio: aspectRatio || 'auto',
        }}
        onLoad={handleLoad}
        onError={handleError}
        wrapperClassName="w-full h-full"
      />
    </picture>
  );
}

/**
 * Skeleton loader for images while they load
 */
export function ImageSkeleton({ className, aspectRatio }: { className?: string; aspectRatio?: string }) {
  return (
    <div
      className={cn(
        'animate-pulse bg-gray-200',
        className
      )}
      style={{ aspectRatio: aspectRatio || 'auto' }}
    >
      <div className="w-full h-full flex items-center justify-center">
        <svg
          className="w-12 h-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    </div>
  );
}
